import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getPlatformCostForTender } from '../../services/billingService';
import { updateTenderInsights } from '../../services/tenderAIService';
import type { Tender } from '../../types/tender';

const EMPTY_ARRAY: never[] = [];

interface ArchiveDetailModalProps {
  open: boolean;
  onClose: () => void;
  tender: Tender | null;
  usersMap?: Record<string, { displayName: string; teamId?: string }>;
  uniqueIssuers?: string[];
}

const BID_TYPES = ['Public tender', 'Private tender', 'Panel bid', 'RFP', 'RFI', 'RFQ', 'Other'];
const CATEGORIES = [
  'Won',
  'Lost',
  'Not Relevant',
  'Already Actioned',
  "Didn't respond in time",
  'Duplicate',
  'Not enough capacity',
  'Not cost effective',
  "Couldn't find appropriate partner",
  'Not aligned to capability',
  'Not aligned to strategic position / direction',
  'Unacceptable contract terms',
  'Other',
];
const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT', 'NATIONAL'];

const ArchiveDetailModal: React.FC<ArchiveDetailModalProps> = ({
  open,
  onClose,
  tender,
  usersMap = {},
  uniqueIssuers = [],
}) => {
  const { showSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [platformCost, setPlatformCost] = useState(0);

  const { account } = useAccount();
  const [formData, setFormData] = useState({
    title: '',
    tenderIssuer: '',
    state: '',
    bidType: '',
    bidCost: '',
    proposedFee: '',
    tenderValue: '',
    category: '',
    reasonDetails: '',
    collaborators: [] as string[],
    includeInMetrics: true,
  });

  useEffect(() => {
    if (tender?.id && account?.clientId && open) {
      const fetchCost = async () => {
        try {
          const cost = await getPlatformCostForTender(account.clientId, tender);
          setPlatformCost(cost);
        } catch (_error) {
          // Ignore
        }
      };
      fetchCost();
    } else {
      setPlatformCost(0);
    }
  }, [tender?.id, tender?.tenderId, tender?.sourceStoreTenderId, account?.clientId, open, tender]);

  useEffect(() => {
    if (tender && open) {
      // Parse category and reason from archiveReason if possible
      let initialCategory = '';
      let initialReason = tender.archiveReason || tender.lossReason || '';
      for (const cat of CATEGORIES) {
        if (initialReason.startsWith(`${cat} - `)) {
          initialCategory = cat;
          initialReason = initialReason.substring(cat.length + 3);
          break;
        } else if (initialReason === cat) {
          initialCategory = cat;
          initialReason = '';
          break;
        }
      }

      setFormData({
        title: tender.title || '',
        tenderIssuer: tender.tenderIssuer || '',
        state: tender.state || '',
        bidType: tender.bidType || '',
        bidCost:
          tender.bidCost !== undefined && tender.bidCost !== null ? tender.bidCost.toString() : '',
        proposedFee:
          tender.proposedFee !== undefined && tender.proposedFee !== null
            ? tender.proposedFee.toString()
            : '',
        tenderValue:
          tender.tenderValue !== undefined && tender.tenderValue !== null
            ? tender.tenderValue.toString()
            : '',
        category: initialCategory,
        reasonDetails: initialReason,
        collaborators: tender.sharedWith || EMPTY_ARRAY,
        includeInMetrics: tender.includeInMetrics !== false,
      });
    }
  }, [tender, open]);

  if (!tender) {
    return null;
  }

  const handleChange =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleCheckboxChange =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
    };

  const handleCollaboratorsChange = (_event: React.SyntheticEvent, newValue: string[]) => {
    setFormData((prev) => ({ ...prev, collaborators: newValue }));
  };

  const getWordCount = (text: string) => (text.trim() ? text.trim().split(/\s+/).length : 0);
  const wordCount = getWordCount(formData.reasonDetails);
  const isReasonError = wordCount > 500;

  const handleSave = async () => {
    if (!tender.id) {
      return;
    }
    if (isReasonError) {
      showSnackbar('Reason exceeds 500 words.', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const finalArchiveReason = formData.category
        ? `${formData.category} - ${formData.reasonDetails.trim()}`
        : formData.reasonDetails.trim();

      await updateTenderInsights(tender.id, {
        title: formData.title || 'Untitled Tender',
        tenderIssuer: formData.tenderIssuer || null,
        state: formData.state || null,
        bidType: formData.bidType || null,
        bidCost: formData.bidCost !== '' ? Number(formData.bidCost) : null,
        proposedFee: formData.proposedFee !== '' ? Number(formData.proposedFee) : null,
        tenderValue: formData.tenderValue !== '' ? Number(formData.tenderValue) : null,
        archiveReason: finalArchiveReason || null,
        lossReason: finalArchiveReason || null, // Keeping it synced just in case
        sharedWith: formData.collaborators,
        includeInMetrics: formData.includeInMetrics,
      });
      showSnackbar('Tender updated successfully.', 'success');
      onClose();
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(err.message || 'Failed to update tender.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Prepare users for Autocomplete options
  const userOptions = Object.entries(usersMap)
    .filter(([id]) => id !== tender.ownerId) // exclude owner from collaborators
    .map(([id, user]) => ({
      id,
      label: user.displayName,
    }));

  // For the Issuer auto-fill, we pass down unique issuers from the parent.
  const issuerOptions: string[] = uniqueIssuers;

  const canEdit =
    tender.ownerId === account?.uid ||
    account?.cachedPermissions?.includes('tender.view_client') ||
    account?.cachedPermissions?.includes('tender.manage_team') ||
    account?.cachedPermissions?.includes('system.manage');

  const isDisabled = isSaving || !canEdit;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="archive-detail-title"
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Typography id="archive-detail-title" variant="h6" component="div" sx={{ pr: 4 }}>
          Edit Archived Tender
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isSaving}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 1 }}>
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              General Details
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                mt: 1,
              }}
            >
              <TextField
                label="Tender Title"
                value={formData.title}
                onChange={handleChange('title')}
                fullWidth
                size="small"
                disabled={isDisabled}
              />
              <Autocomplete
                freeSolo
                options={issuerOptions}
                value={formData.tenderIssuer}
                onInputChange={(_, newValue) => {
                  setFormData((prev) => ({ ...prev, tenderIssuer: newValue }));
                }}
                disabled={isDisabled}
                renderInput={(params) => (
                  <TextField {...params} label="Issuer / Client" fullWidth size="small" />
                )}
              />
              <TextField
                select
                label="State / Location"
                value={formData.state}
                onChange={handleChange('state')}
                fullWidth
                size="small"
                disabled={isDisabled}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {STATES.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Bid type"
                value={formData.bidType}
                onChange={handleChange('bidType')}
                fullWidth
                size="small"
                disabled={isDisabled}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {BID_TYPES.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Financials
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 2,
                mt: 1,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }}>
                  Time and resources outside of the BIDLOGIQ platform
                </Typography>
                <TextField
                  label="Bid cost"
                  type="number"
                  value={formData.bidCost}
                  onChange={handleChange('bidCost')}
                  fullWidth
                  size="small"
                  disabled={isDisabled}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Bid Cost
                    </Typography>
                    <Typography variant="body2">
                      $
                      {Number(formData.bidCost || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      + Platform Cost
                    </Typography>
                    <Typography variant="body2">
                      $
                      {platformCost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      pt: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      Total Cost
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      $
                      {(Number(formData.bidCost || 0) + platformCost).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }} />
                <TextField
                  label="Proposed fee"
                  type="number"
                  value={formData.proposedFee}
                  onChange={handleChange('proposedFee')}
                  fullWidth
                  size="small"
                  disabled={isDisabled}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }}>
                  The actual value of the project
                </Typography>
                <TextField
                  label="Tender value"
                  type="number"
                  value={formData.tenderValue}
                  onChange={handleChange('tenderValue')}
                  fullWidth
                  size="small"
                  disabled={isDisabled}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Archive Reason & Metrics
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.includeInMetrics}
                    onChange={handleCheckboxChange('includeInMetrics')}
                    color="primary"
                    disabled={isDisabled}
                  />
                }
                label="Include in metrics and insights"
              />
              <TextField
                select
                label="Category"
                value={formData.category}
                onChange={handleChange('category')}
                fullWidth
                size="small"
                disabled={isDisabled}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {CATEGORIES.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Reason Details"
                multiline
                minRows={3}
                maxRows={8}
                value={formData.reasonDetails}
                onChange={handleChange('reasonDetails')}
                fullWidth
                size="small"
                error={isReasonError}
                disabled={isDisabled}
                helperText={
                  isReasonError
                    ? `Maximum 500 words exceeded (${wordCount}/500)`
                    : `${wordCount}/500 words`
                }
                inputProps={{
                  style: { resize: 'vertical' },
                }}
              />
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Collaborators
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Autocomplete
                multiple
                options={userOptions.map((o) => o.id)}
                getOptionLabel={(optionId) => usersMap[optionId]?.displayName || optionId}
                value={formData.collaborators}
                onChange={handleCollaboratorsChange}
                disabled={isDisabled}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        variant="outlined"
                        label={usersMap[option]?.displayName || option}
                        size="small"
                        key={key}
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Collaborators" placeholder="Select team members" />
                )}
                size="small"
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isSaving}>
          {canEdit ? 'Cancel' : 'Close'}
        </Button>
        {canEdit && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving || isReasonError}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : undefined}
            sx={(theme) => ({
              backgroundColor: theme.palette.bidlogiq?.orange || '#FFA500',
              '&:hover': {
                backgroundColor: '#FF8C00',
              },
            })}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ArchiveDetailModal;
